(() => {
  const isEnabled = (() => {
    try {
      return new URLSearchParams(window.location.search).get("mindelor_drawer_debug") === "1" ||
        window.localStorage?.getItem("mindelorDrawerDebug") === "1";
    } catch (error) {
      return false;
    }
  })();

  if (!isEnabled) {
    return;
  }

  const LOG_LIMIT = 500;
  const componentDefinitions = [
    { selector: "header-search", tagName: "header-search", componentId: "header-search" },
    { selector: "#sidebar-menu.header-sidebar", tagName: "header-sidebar", componentId: "sidebar-menu" },
    { selector: "cart-drawer#cart-drawer", tagName: "cart-drawer", componentId: "cart-drawer" }
  ];
  const dialogEvents = [
    "dialog:before-show",
    "dialog:after-show",
    "dialog:before-hide",
    "dialog:after-hide",
    "dialog:force-close",
    "toggle"
  ];
  const styleProperties = ["display", "visibility", "opacity", "transform"];
  const relevantEvents = [];
  const pointerStates = new WeakMap();
  const observedTargets = new WeakSet();
  const observedHeaders = new WeakSet();
  const wrappedPrototypes = new Map();

  window.__mindelorDrawerDebugLog = Array.isArray(window.__mindelorDrawerDebugLog)
    ? window.__mindelorDrawerDebugLog
    : [];

  const timestamp = () => new Date().toISOString();

  const getStack = () => {
    try {
      return new Error().stack || null;
    } catch (error) {
      return null;
    }
  };

  const describeNode = (node) => {
    if (!node || typeof node !== "object") {
      return null;
    }

    try {
      return {
        tag: node.tagName?.toLowerCase?.() || node.nodeName || null,
        id: node.id || null,
        className: typeof node.className === "string" ? node.className : null
      };
    } catch (error) {
      return null;
    }
  };

  const getDefinition = (element) => componentDefinitions.find((definition) => {
    try {
      return element?.matches?.(definition.selector);
    } catch (error) {
      return false;
    }
  }) || null;

  const getContent = (element) => {
    try {
      return element?.getShadowPartByName?.("content") ||
        element?.shadowRoot?.querySelector?.('[part="content"]') ||
        null;
    } catch (error) {
      return null;
    }
  };

  const getHeader = (element) => {
    try {
      return element?.closest?.("x-header") || document.querySelector("x-header");
    } catch (error) {
      return null;
    }
  };

  const getState = (element) => {
    let state = pointerStates.get(element);

    if (!state) {
      state = {
        insideHost: false,
        insideContent: false,
        lastPoint: null
      };
      pointerStates.set(element, state);
    }

    return state;
  };

  const openState = (element) => {
    let open = null;

    try {
      open = typeof element?.open === "boolean" ? element.open : null;
    } catch (error) {
    }

    return {
      attribute: Boolean(element?.hasAttribute?.("open")),
      property: open
    };
  };

  const eventPath = (event) => {
    try {
      return event?.composedPath?.() || [];
    } catch (error) {
      return [];
    }
  };

  const pathIncludes = (path, element) => path.includes(element);

  const isPathInside = (path, element) => {
    const content = getContent(element);

    return pathIncludes(path, element) || Boolean(content && pathIncludes(path, content));
  };

  const activeElementDetails = () => {
    try {
      return describeNode(document.activeElement);
    } catch (error) {
      return null;
    }
  };

  const pointerDetails = (element) => {
    const state = getState(element);
    let pointTarget = null;

    if (state.lastPoint && Number.isFinite(state.lastPoint.x) && Number.isFinite(state.lastPoint.y)) {
      try {
        pointTarget = document.elementFromPoint(state.lastPoint.x, state.lastPoint.y);
      } catch (error) {
      }
    }

    return {
      insideHost: state.insideHost,
      insideContent: state.insideContent,
      lastPoint: state.lastPoint,
      elementFromPoint: describeNode(pointTarget)
    };
  };

  const headerDetails = (element) => {
    const header = getHeader(element);

    return {
      element: describeNode(header),
      isHidden: Boolean(header?.classList?.contains?.("is-hidden"))
    };
  };

  const trimRelevantEvents = () => {
    while (relevantEvents.length > 50) {
      relevantEvents.shift();
    }
  };

  const addLog = (element, eventName, details = {}) => {
    try {
      const definition = getDefinition(element);
      const entry = {
        timestamp: timestamp(),
        event: eventName,
        componentId: definition?.componentId || element?.id || null,
        componentTag: element?.tagName?.toLowerCase?.() || null,
        open: openState(element),
        ...details
      };

      window.__mindelorDrawerDebugLog.push(entry);

      if (window.__mindelorDrawerDebugLog.length > LOG_LIMIT) {
        window.__mindelorDrawerDebugLog.splice(0, window.__mindelorDrawerDebugLog.length - LOG_LIMIT);
      }

      try {
        console.debug("[MINDELOR drawer debug]", JSON.stringify(entry));
      } catch (error) {
      }

      return entry;
    } catch (error) {
      return null;
    }
  };

  const addRelevantEvent = (element, eventName, event, extra = {}) => {
    const path = eventPath(event);
    const state = getState(element);
    const content = getContent(element);
    const insideHost = isPathInside(path, element);
    const insideContent = Boolean(content && pathIncludes(path, content));

    if (eventName === "pointerenter") {
      state.insideHost = true;
      state.insideContent = insideContent || state.insideContent;
    } else if (eventName === "pointerleave") {
      state.insideHost = false;
      state.insideContent = false;
    } else if (eventName === "pointerdown") {
      state.insideHost = insideHost;
      state.insideContent = insideContent;
    }

    if (event?.clientX !== undefined && event?.clientY !== undefined) {
      state.lastPoint = {
        x: event.clientX,
        y: event.clientY
      };
    }

    const item = {
      timestamp: timestamp(),
      event: eventName,
      componentId: getDefinition(element)?.componentId || element?.id || null,
      target: describeNode(event?.target),
      insideHost,
      insideContent,
      key: event?.key || null,
      x: Number.isFinite(event?.clientX) ? event.clientX : null,
      y: Number.isFinite(event?.clientY) ? event.clientY : null,
      ...extra
    };

    relevantEvents.push(item);
    trimRelevantEvents();

    addLog(element, eventName, {
      stack: getStack(),
      target: item.target,
      insideHost,
      insideContent,
      key: item.key,
      x: item.x,
      y: item.y,
      activeElement: activeElementDetails(),
      header: headerDetails(element)
    });
  };

  const hideSnapshot = (element) => ({
    stack: getStack(),
    _allowNextHide: element?._allowNextHide === undefined ? null : element._allowNextHide,
    _mindelorAllowClose: element?._mindelorAllowClose === undefined ? null : element._mindelorAllowClose,
    _mindelorExplicitClose: element?._mindelorExplicitClose === undefined ? null : element._mindelorExplicitClose,
    activeElement: activeElementDetails(),
    pointer: pointerDetails(element),
    header: headerDetails(element),
    lastRelevantEvents: relevantEvents.slice(-10)
  });

  const relevantTargetsForEvent = (event) => {
    const path = eventPath(event);

    return componentDefinitions
      .map((definition) => ({
        definition,
        element: document.querySelector(definition.selector)
      }))
      .filter(({ element }) => {
        if (!element) {
          return false;
        }

        const hasControlPath = path.some((node) => {
          try {
            return node?.getAttribute?.("aria-controls") === element.id;
          } catch (error) {
            return false;
          }
        });

        return Boolean(element.open || path.includes(element) || hasControlPath);
      });
  };

  const recordGlobalEvent = (event) => {
    if (event.type === "keydown" && !["Escape", "Esc"].includes(event.key)) {
      return;
    }

    relevantTargetsForEvent(event).forEach(({ element }) => {
      addRelevantEvent(element, event.type === "keydown" ? "keydown:Escape" : event.type, event, {
        outsideTarget: !isPathInside(eventPath(event), element)
      });
    });
  };

  window.addEventListener("pointerdown", recordGlobalEvent, true);
  window.addEventListener("click", recordGlobalEvent, true);
  window.addEventListener("focusin", recordGlobalEvent, true);
  window.addEventListener("focusout", recordGlobalEvent, true);
  window.addEventListener("keydown", recordGlobalEvent, true);

  const observeStyleMutation = (element, mutation) => {
    const oldStyle = document.createElement("div").style;
    const newStyle = document.createElement("div").style;

    try {
      oldStyle.cssText = mutation.oldValue || "";
      newStyle.cssText = mutation.target.getAttribute("style") || "";
    } catch (error) {
      return;
    }

    const changed = styleProperties.reduce((properties, property) => {
      const oldValue = oldStyle.getPropertyValue(property);
      const newValue = newStyle.getPropertyValue(property);

      if (oldValue !== newValue) {
        properties[property] = { oldValue, newValue };
      }

      return properties;
    }, {});

    if (Object.keys(changed).length > 0) {
      addLog(element, "mutation:inline-style", {
        mutatedNode: describeNode(mutation.target),
        properties: changed,
        stack: getStack()
      });
    }
  };

  const observeTarget = (element) => {
    if (!element || observedTargets.has(element)) {
      return;
    }

    observedTargets.add(element);
    getState(element);

    const observeRoot = (root) => {
      if (!root) {
        return;
      }

      new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type !== "attributes") {
            return;
          }

          if (mutation.attributeName === "open" && mutation.target === element) {
            addLog(element, "mutation:open", {
              oldValue: mutation.oldValue,
              newValue: element.getAttribute("open"),
              stack: getStack()
            });
          }

          if (mutation.attributeName === "style") {
            observeStyleMutation(element, mutation);
          }
        });
      }).observe(root, {
        attributes: true,
        attributeOldValue: true,
        subtree: true,
        attributeFilter: ["open", "style"]
      });
    };

    observeRoot(element);
    observeRoot(element.shadowRoot);

    dialogEvents.forEach((eventName) => {
      element.addEventListener(eventName, (event) => {
        addLog(element, eventName, {
          stack: getStack(),
          target: describeNode(event.target),
          activeElement: activeElementDetails(),
          pointer: pointerDetails(element),
          header: headerDetails(element)
        });
      }, true);
    });

    [element, getContent(element)].filter(Boolean).forEach((pointerTarget) => {
      pointerTarget.addEventListener("pointerenter", (event) => {
        addRelevantEvent(element, "pointerenter", event, {
          source: describeNode(pointerTarget)
        });
      });
      pointerTarget.addEventListener("pointerleave", (event) => {
        addRelevantEvent(element, "pointerleave", event, {
          source: describeNode(pointerTarget)
        });
      });
    });

    const header = getHeader(element);

    if (header && !observedHeaders.has(header)) {
      observedHeaders.add(header);
      new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type !== "attributes" || mutation.attributeName !== "class") {
            return;
          }

          const oldHidden = (mutation.oldValue || "").split(/\s+/).includes("is-hidden");
          const newHidden = header.classList.contains("is-hidden");

          if (oldHidden !== newHidden) {
            addLog(element, "mutation:header-is-hidden", {
              header: describeNode(header),
              oldValue: oldHidden,
              newValue: newHidden,
              stack: getStack()
            });
          }
        });
      }).observe(header, {
        attributes: true,
        attributeOldValue: true,
        attributeFilter: ["class"]
      });
    }
  };

  const wrappedSetFor = (methodName) => {
    if (!wrappedPrototypes.has(methodName)) {
      wrappedPrototypes.set(methodName, new WeakSet());
    }

    return wrappedPrototypes.get(methodName);
  };

  const wrapPrototypeMethod = (Ctor, methodName) => {
    const prototype = Ctor?.prototype;

    if (!prototype || wrappedSetFor(methodName).has(prototype)) {
      return;
    }

    const original = prototype[methodName];

    if (typeof original !== "function") {
      return;
    }

    wrappedSetFor(methodName).add(prototype);

    prototype[methodName] = function(...args) {
      const element = this;

      if (methodName === "hide") {
        addLog(element, "method:hide", hideSnapshot(element));
      } else if (methodName === "show") {
        addLog(element, "method:show", {
          args: Array.from(args),
          stack: getStack()
        });
      } else if (methodName === "hideForOutsideClickTarget") {
        addLog(element, "method:hideForOutsideClickTarget:before", {
          argumentTarget: describeNode(args[0]),
          stack: getStack(),
          pointer: pointerDetails(element),
          header: headerDetails(element)
        });
      } else if (methodName === "attributeChangedCallback") {
        addLog(element, "method:attributeChangedCallback", {
          args: Array.from(args),
          stack: getStack()
        });
      }

      return original.apply(this, args);
    };
  };

  const setupDefinition = async (definition) => {
    await window.customElements.whenDefined(definition.tagName);

    const Ctor = window.customElements.get(definition.tagName);

    ["show", "hide", "hideForOutsideClickTarget", "attributeChangedCallback"].forEach((methodName) => {
      wrapPrototypeMethod(Ctor, methodName);
    });

    document.querySelectorAll(definition.selector).forEach(observeTarget);
  };

  componentDefinitions.forEach((definition) => {
    setupDefinition(definition).catch((error) => {
      addLog(null, "diagnostic:setup-error", {
        componentId: definition.componentId,
        message: String(error),
        stack: getStack()
      });
    });
  });

  window.mindelorCopyDrawerDebugLog = () => {
    const log = window.__mindelorDrawerDebugLog;
    const json = JSON.stringify(log, null, 2);

    try {
      window.navigator.clipboard?.writeText(json)?.catch(() => {
      });
    } catch (error) {
    }

    return log;
  };

  console.info("[MINDELOR drawer debug] enabled");
})();
